// Browser speech utilities: text-to-speech + speech-to-text (no keys, no cost).
export const speechSupported = () => typeof window !== 'undefined' && ('speechSynthesis' in window);
export const recognitionSupported = () => typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

export function listVoices() {
  try { return (window.speechSynthesis.getVoices() || []).filter((v) => v.lang && v.lang.startsWith('en')); }
  catch (e) { return []; }
}

// Curated, human-friendly voice "tones". Each maps to a browser voice via a name
// pattern, plus pitch/rate tuning so tones sound distinct even on the same device.
export const VOICE_TONES = [
  { id: 'warm',   label: 'Warm',   hint: 'Friendly & reassuring', match: /samantha|jenny|aria|google us english|susan|female|zira/i, pitch: 1.0,  rate: 1.0,  sample: "Hey, I'm right here with you. Let's take this one step at a time." },
  { id: 'bright', label: 'Bright', hint: 'Upbeat & energetic',    match: /aria|jenny|google us english|female|zira|samantha/i,        pitch: 1.18, rate: 1.1,  sample: "Love it! Let's knock this out — I think you've totally got this." },
  { id: 'calm',   label: 'Calm',   hint: 'Slow & soothing',        match: /samantha|google uk english female|serena|female|karen/i,     pitch: 0.96, rate: 0.9,  sample: "Take a breath. There's no rush. We'll get there together." },
  { id: 'deep',   label: 'Deep',   hint: 'Grounded & steady',      match: /daniel|alex|david|google uk english male|male|fred/i,        pitch: 0.82, rate: 0.98, sample: "Alright. Here's the plan — clear, simple, and doable." },
];

export function getTone(id) {
  return VOICE_TONES.find((t) => t.id === id) || null;
}

// Resolve a browser SpeechSynthesisVoice for a given tone id.
function resolveVoice(voices, tone) {
  if (tone) {
    const byPattern = voices.find((v) => v.lang?.startsWith('en') && tone.match.test(v.name));
    if (byPattern) return byPattern;
  }
  return voices.find((v) => /female|samantha|google us english|jenny|aria/i.test(v.name)) || voices.find((v) => v.lang?.startsWith('en'));
}

export function speak(text, { onEnd, voice, rate } = {}) {
  try {
    if (!speechSupported()) { onEnd && onEnd(); return; }
    window.speechSynthesis.cancel();
    const clean = (text || '').replace(/[#*`_>~]/g, '').replace(/\[\[CALL[^\]]*\]\]/g, '').slice(0, 700);
    const u = new SpeechSynthesisUtterance(clean);
    const tone = getTone(voice);
    const baseRate = tone ? tone.rate : 1.02;
    const mult = (typeof rate === 'number' && rate > 0) ? rate : 1.0;
    u.rate = Math.max(0.5, Math.min(2, baseRate * mult));
    u.pitch = tone ? tone.pitch : 1.0;
    const voices = window.speechSynthesis.getVoices();
    const chosen = resolveVoice(voices, tone);
    if (chosen) u.voice = chosen;
    u.onend = () => onEnd && onEnd();
    u.onerror = () => onEnd && onEnd();
    window.speechSynthesis.speak(u);
  } catch (e) { onEnd && onEnd(); }
}

export function stopSpeaking() {
  try { window.speechSynthesis.cancel(); } catch (e) {}
}

// Create a recognizer. onResult(finalText), onInterim(text), onEnd().
export function createRecognizer({ onResult, onInterim, onEnd, continuous = false } = {}) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const rec = new SR();
  rec.lang = 'en-US';
  rec.interimResults = true;
  rec.continuous = continuous;
  rec.onresult = (e) => {
    let interim = '', finalText = '';
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const t = e.results[i][0].transcript;
      if (e.results[i].isFinal) finalText += t; else interim += t;
    }
    if (interim && onInterim) onInterim(interim);
    if (finalText && onResult) onResult(finalText.trim());
  };
  rec.onend = () => onEnd && onEnd();
  rec.onerror = () => onEnd && onEnd();
  return rec;
}
